import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';

/**
 * Estrutura de item devolvida por GET /api/v1/menu.
 * O MenuRegistry devolve: { module: [ { label, route, icon } ] }
 *
 * A resposta é convertida em uma lista e os nomes de rotas são associados aos caminhos.
 */
export interface ApiMenuItem {
  label: string;
  route: string;   // Nome interno da rota Laravel
  icon: string;    // Lucide icon name string (e.g. 'LayoutDashboard')
  module?: string;
  badge?: number;
}

export interface MenuResponse {
  data: Record<string, ApiMenuItem[]> | ApiMenuItem[];
}

/**
 * Associa os nomes internos de rota aos caminhos da interface.
 * Inclui somente rotas diferentes da navegação estática.
 */
const ROUTE_TO_PATH: Record<string, string> = {
  'dashboard':        '/visao-geral',
  'bookings.index':   '/reservas',
  'calendar.index':   '/ocupacao',
  'guests.index':     '/hospedes',
  'rooms.index':      '/ocupacao',
  'housekeeping':     '/governanca',
  'finance':          '/financeiro',
  'night-audit':      '/auditoria-noturna',
  'channel-manager':  '/gerenciador-de-canais',
  'settings':         '/configuracoes',
  'anomalies':        '/alertas-operacionais',
  'maintenance':      '/manutencoes',
  'releases':         '/versoes',
  'routines':         '/rotinas',
  'inventory':        '/estoque-e-consumo',
  'admin.queues':     '/administracao/filas',
  'admin.permissions':'/administracao/permissoes',
};

export function routeToPath(route: string): string | null {
  return ROUTE_TO_PATH[route] ?? null;
}

/**
 * Devolve os itens de menu da API em uma lista.
 * Usa a navegação estática quando a API está indisponível.
 */
export function useMenuQuery() {
  return useQuery<ApiMenuItem[]>({
    queryKey: ['menu'],
    queryFn: async (): Promise<ApiMenuItem[]> => {
      const { data } = await api.get<MenuResponse>('/v1/menu');
      const grouped = data.data ?? {};
      if (Array.isArray(grouped)) return grouped;

      // Converte em lista e acrescenta o nome do módulo
      const flat: ApiMenuItem[] = [];
      for (const [module, items] of Object.entries(grouped)) {
        for (const item of items) {
          flat.push({ ...item, module });
        }
      }

      return flat;
    },
    staleTime: 5 * 60_000,        // 5 min — menu doesn't change often
    retry: false,                   // não repete em caso de erro; usa a navegação estática
    throwOnError: false,            // trata o erro e devolve uma lista vazia
  });
}
